import { useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";

import { ServiceTicket } from "../types";

export interface SpareReturnLine {
  sparePartId: string;
  sparePartName: string;
  partCode: string;
  issuedQty: number;
  usedQty: number;
  returnedQty: number;
  acceptedQty: number;
  damagedQty: number;
  remarks?: string;
}

export interface SpareReturnForm {
  remarks?: string;
  items: SpareReturnLine[];
}

interface SpareReturnDialogProps {
  open: boolean;
  loading?: boolean;
  ticket?: ServiceTicket | null;
  items: SpareReturnLine[];
  onClose: () => void;
  onSubmit: (payload: SpareReturnForm) => void;
}

const SpareReturnDialog = ({
  open,
  loading = false,
  ticket,
  items,
  onClose,
  onSubmit,
}: SpareReturnDialogProps) => {
  const methods = useForm<SpareReturnForm>({
    defaultValues: {
      remarks: "",
      items,
    },
  });

  const { control, handleSubmit, reset, setValue, watch } = methods;
  const { fields } = useFieldArray({
    control,
    name: "items",
  });

  useEffect(() => {
    reset({
      remarks: "",
      items,
    });
  }, [items, reset]);

  const watchedItems = watch("items");
  const rowError = (item: SpareReturnLine) => {
    const issuedQty = Number(item.issuedQty || 0);
    const usedQty = Number(item.usedQty || 0);
    const returnedQty = Number(item.returnedQty || 0);
    if ([usedQty, returnedQty].some((value) => value < 0)) {
      return "Quantities cannot be negative.";
    }

    if (usedQty + returnedQty !== issuedQty) {
      return `Used + Returned must equal Issued (${issuedQty}).`;
    }

    return "";
  };

  const hasInvalidRows = watchedItems.some((item) => {
    const issuedQty = Number(item.issuedQty || 0);
    const usedQty = Number(item.usedQty || 0);
    const returnedQty = Number(item.returnedQty || 0);

    return (
      returnedQty < 0 ||
      usedQty < 0 ||
      usedQty + returnedQty !== issuedQty
    );
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Spare Usage and Return</DialogTitle>
      <DialogContent dividers>
        <FormProvider {...methods}>
          <Stack spacing={3}>
            {ticket && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Ticket
                  </Typography>
                  <Typography fontWeight={800}>{ticket.ticketNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Vehicle
                  </Typography>
                  <Typography fontWeight={800}>{ticket.vehicleNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Technician
                  </Typography>
                  <Typography fontWeight={800}>
                    {ticket.assignedTechnicianName || ticket.assignedTechnicianId || "-"}
                  </Typography>
                </Grid>
              </Grid>
            )}

            {!items.length && (
              <Alert severity="info">
                No issued spare balance is available for return on this ticket.
              </Alert>
            )}

            {fields.map((field, index) => (
              <Grid container spacing={2} alignItems="center" key={field.id}>
                {rowError(watchedItems[index]) && (
                  <Grid item xs={12}>
                    <Alert severity="warning">{rowError(watchedItems[index])}</Alert>
                  </Grid>
                )}

                <Grid item xs={12} md={3}>
                  <Typography fontWeight={800}>{field.sparePartName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {field.partCode}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={1.2}>
                  <TextField
                    value={field.issuedQty}
                    label="Issued"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={6} md={1.4}>
                  <Controller
                    name={`items.${index}.usedQty`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Used"
                        fullWidth
                        onChange={(event) => {
                          const usedQty = Number(event.target.value || 0);
                          const issuedQty = Number(watchedItems[index]?.issuedQty || 0);
                          const returnedQty = Math.max(0, issuedQty - usedQty);

                          field.onChange(usedQty);
                          setValue(`items.${index}.returnedQty`, returnedQty, {
                            shouldValidate: true,
                          });
                          setValue(`items.${index}.acceptedQty`, returnedQty, {
                            shouldValidate: true,
                          });
                          setValue(`items.${index}.damagedQty`, 0, {
                            shouldValidate: true,
                          });
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={1.4}>
                  <Controller
                    name={`items.${index}.returnedQty`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Returned"
                        fullWidth
                        onChange={(event) => {
                          const returnedQty = Number(event.target.value || 0);
                          const issuedQty = Number(watchedItems[index]?.issuedQty || 0);

                          field.onChange(returnedQty);
                          setValue(
                            `items.${index}.usedQty`,
                            Math.max(0, issuedQty - returnedQty),
                            { shouldValidate: true }
                          );
                          setValue(`items.${index}.acceptedQty`, returnedQty, {
                            shouldValidate: true,
                          });
                          setValue(`items.${index}.damagedQty`, 0, {
                            shouldValidate: true,
                          });
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name={`items.${index}.remarks`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Remarks" fullWidth />
                    )}
                  />
                </Grid>
              </Grid>
            ))}

            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Usage and Return Remarks" fullWidth multiline minRows={2} />
              )}
            />
          </Stack>
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={loading || !items.length || hasInvalidRows}
        >
          {loading ? "Submitting..." : "Submit Usage and Return"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpareReturnDialog;
